import {
  FindingSource,
  QualifiedFeature,
  ValidationStatus,
} from "fe-common/graphql";
import React from "react";
import {
  Box,
  ListItemText,
  styled,
  SvgIcon,
  Theme,
  Tooltip,
} from "@mui/material";
import { getQFAndRefinementsLines } from "fe-common/logic";
import { findingFormat } from "fe-web-components/formatting/FindingNameFormatter";
import clsx from "clsx";

import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
//region [[ Styles ]]

const FindingItemText = styled(ListItemText)({
  "margin-bottom": "6px",
});

const FindingContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
});

const FindingItemTypography = styled("span")({
  fontSize: "16px",
  fontWeight: 400,
  color: "black",
  //fontFamily: CONDENSED_FONT_LT,
});

const LeftSide = styled("div")({
  display: "flex",
  alignItems: "center",
});

const FindingListItem = styled("div")(
  (props: {
    theme?: Theme;
    indented: "true" | "false";
    clickable: "true" | "false";
  }) => ({
    alignItems: "center",
    paddingRight: "60px",
    paddingLeft: props.indented === "true" ? "30px" : "16px",
    cursor: props.clickable === "true" ? "pointer" : "unset",
    //pointerEvents: props.clickable === "true" ? "unset" : "none",
  }),
);

const RefinementContainer = styled("span")({});

const FindingRefinementsItemTypography = styled("span")({
  fontSize: "14px",
  fontWeight: 400,
  color: "#4b4b4b",
  //fontFamily: CONDENSED_FONT_LT,
});

const FindingSourceSpan = styled("span")({
  fontSize: "10px",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientChartFindingProps {
  qf: QualifiedFeature;
  index: number;
  isLast: boolean;
  isEditable: boolean;
  indented?: boolean;
  onClick?: () => void;
  source?: FindingSource;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

const PatientChartFinding = ({ ...props }: PatientChartFindingProps) => {
  const [findingTitle, findingQualifiersText] = getQFAndRefinementsLines(
    props.qf,
  );

  // @todo - bring back when working on feature of change yes no popper
  //const change = extractResponseFromQF(props.qf);

  const clickable = props.onClick ? true : false;

  return (
    <FindingListItem
      className={"patient-chart-finding"}
      key={props.qf?.id}
      indented={props.indented ? "true" : "false"}
      clickable={clickable ? "true" : "false"}
      onClick={props.onClick}
    >
      <FindingContainer>
        {/* @todo - bring back when working on feature of change yes no popper*/}
        {/*<YesNoIndicator negate={change.negate} level={change.level} />*/}
        {/* todo - cancel No from finding title in a normal way" */}
        <FindingItemText
          key={findingTitle}
          primary={
            <LeftSide>
              <FindingItemTypography className={"finding-refinements-item"}>
                {findingFormat(props.qf)}
                {props.source && (
                  <FindingSourceSpan>{" " + props.source}</FindingSourceSpan>
                )}
              </FindingItemTypography>
            </LeftSide>
          }
          secondary={
            <RefinementContainer className={"refinement-container"}>
              {findingQualifiersText && (
                <FindingRefinementsItemTypography
                  className={"finding-refinements-item"}
                >
                  {findingQualifiersText}
                </FindingRefinementsItemTypography>
              )}
            </RefinementContainer>
          }
        />
      </FindingContainer>
    </FindingListItem>
  );
};

export interface DemoFindingItemProps {
  className?: string;
  qf: QualifiedFeature;
  idx?: number;
  onClick?: (qf: QualifiedFeature) => any;
  children?: React.ReactNode;
  source?: FindingSource;
  validation?: ValidationStatus;
}

export const DemoFindingItem = ({
  qf,
  idx,
  onClick,
  children,
  ...props
}: DemoFindingItemProps) => {
  return (
    <Box
      className={clsx(props.className, "demo-finding-item")}
      flexDirection="row"
      display="flex"
      alignItems="center"
      alignContent="center"
    >
      <PatientChartFinding
        qf={qf}
        index={idx}
        isLast={false}
        isEditable={false}
        onClick={onClick ? () => onClick(qf) : null}
        source={props.source}
      />
      {children}
    </Box>
  );
};

export interface PatientChartFindingMapProps {
  className?: string;
  qf: QualifiedFeature;
  idx?: number;
  children?: React.ReactNode;
  source?: FindingSource;
  validation?: ValidationStatus;
}

export const PatientChartFindingMap = ({
  qf,
  idx,
  ...props
}: PatientChartFindingMapProps) => {
  return (
    <>
      <DemoFindingItem
        className={clsx(props.className, "patient-chart-finding-map")}
        key={qf?.id}
        source={props.source}
        {...{ qf, idx }}
      >
        {props.children}
        <Box
          flexGrow={1}
          flexDirection="row"
          display="flex"
          justifyContent="flex-end"
        >
          <ValidationStatusDisplay validation={props.validation} />
        </Box>
      </DemoFindingItem>
    </>
  );
};

export function ValidationStatusDisplay({
  validation,
}: {
  validation: ValidationStatus;
}) {
  const text = {
    [ValidationStatus.Validated]: "Validated By Criteria",
    [ValidationStatus.Invalidated]: "Contradicted By Criteria",
    [ValidationStatus.ValidationFollowup]: "Critieria Follow up Question",
  };
  const IconForStatus: Partial<Record<ValidationStatus, typeof SvgIcon>> = {
    [ValidationStatus.Validated]: AssignmentTurnedInOutlinedIcon,
    [ValidationStatus.Invalidated]: AssignmentLateOutlinedIcon,
    [ValidationStatus.ValidationFollowup]: AssignmentIndOutlinedIcon,
  };
  const color = {
    [ValidationStatus.Validated]: "green",
    [ValidationStatus.Invalidated]: "red",
    [ValidationStatus.ValidationFollowup]: "grey",
  };
  if (!validation) return <span />;

  const Icon = IconForStatus[validation];
  return (
    <Tooltip title={text[validation] || ""}>
      {Icon ? (
        <Icon fontSize="small" htmlColor={color[validation] || "white"} />
      ) : (
        <span />
      )}
    </Tooltip>
  );
}
